import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ParticipantListItem from './ParticipantListItem.container';
import ParticipantsListLoader from './ParticipantsListLoader';
import NoResults from './NoResults';
import InfiniteTable from '../../common/InfiniteTable';
import { ListSeparator } from './ParticipantsList.styled';
import RemoveParticipantDialog from './RemoveParticipantDialog.container';

const ParticipantsList = ({
  users,
  sortBy,
  filtered,
  onMessage,
  isFetching,
  hasFetchedAll,
  fetchParticipants,
  removeUserFromSpace,
}) => {
  const [userToRemove, setUserToRemove] = useState(null);
  const [isRemovingUser, setIsRemovingUser] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  useEffect(() => {
    if (!hasFetchedAll && !isFetching && users.length === 0) {
      fetchParticipants({ sort: sortBy });
    }
  });

  const fetchMore = () => {
    if (!isFetching && !hasFetchedAll && !filtered) {
      fetchParticipants({ skip: users.length, sort: sortBy });
    }
  };

  const handleCloseRemoveModal = () => setIsRemoveModalOpen(false);

  const handleOpenRemoveModal = (userId) => {
    setUserToRemove(userId);
    setIsRemoveModalOpen(true);
  };

  const handleRemoveParticipant = async (userId) => {
    setIsRemovingUser(true);
    await removeUserFromSpace(userId);
    setIsRemovingUser(false);
    handleCloseRemoveModal();
  };

  if (isFetching && users.length === 0) {
    return <ParticipantsListLoader />;
  }

  return (
    <Fragment>
      <InfiniteTable
        viewProps={{ 'data-testid': 'participants-list' }}
        items={users}
        keyExtractor={({ id }) => id}
        renderItem={({ id, firstName, lastName, email, avatar }) => (
          <Fragment>
            <ParticipantListItem
              userId={id}
              fullName={`${firstName} ${lastName}`}
              email={email}
              avatar={avatar}
              onMessage={() =>
                onMessage({
                  firstName,
                  lastName,
                  avatar,
                  objectId: id,
                })
              }
              onRemove={() => handleOpenRemoveModal(id)}
            />
            <ListSeparator />
          </Fragment>
        )}
        renderEmptyList={() =>
          filtered ? (
            <NoResults
              primaryText="Looks like no participants match your query."
              secondaryText="Check the spelling or try another!"
            />
          ) : null
        }
        hasNext={!hasFetchedAll}
        loadNext={fetchMore}
        resetScrollOnMount
        contentStyle={{ marginRight: 20 }}
      />
      {isRemoveModalOpen && (
        <RemoveParticipantDialog
          isRemovingUser={isRemovingUser}
          isRemoveModalOpen={isRemoveModalOpen}
          handleRemoveParticipant={handleRemoveParticipant}
          handleCloseRemoveModal={handleCloseRemoveModal}
          userId={userToRemove}
        />
      )}
    </Fragment>
  );
};

ParticipantsList.propTypes = {
  sortBy: PropTypes.string.isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasFetchedAll: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  filtered: PropTypes.bool,
  fetchParticipants: PropTypes.func.isRequired,
  onMessage: PropTypes.func.isRequired,
  removeUserFromSpace: PropTypes.func.isRequired,
};

ParticipantsList.defaultProps = {
  filtered: false,
};

export default ParticipantsList;
